import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Container,
    Checkbox,
    FormControlLabel,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    Alert,
    CircularProgress,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { audienceSignUp, resendAudienceVerificationEmail } from '@virtual-cinema/client-lib';

function SignupPage() {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [isPasswordFocused, setIsPasswordFocused] = useState(false);
    const [showVerificationMessage, setShowVerificationMessage] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isResendLoading, setIsResendLoading] = useState(false);
    const [passwordRequirements, setPasswordRequirements] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
    });

    useEffect(() => {
        setPasswordRequirements({
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /[0-9]/.test(password),
            special: /[@$!%*?&]/.test(password),
        });
    }, [password]);

    const validatePassword = () => {
        return Object.values(passwordRequirements).every(Boolean);
    };

    const validateForm = () => {
        if (!firstName.trim() || !lastName.trim()) {
            setError('First and last names are required.');
            return false;
        }
        if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError('Valid email is required.');
            return false;
        }
        if (!validatePassword()) {
            setError('Password does not meet strength requirements.');
            return false;
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return false;
        }
        if (!acceptTerms) {
            setError('You must accept the terms and conditions.');
            return false;
        }
        setError('');
        return true;
    };

    const handleSignup = async () => {
        if (!validateForm() || isLoading) return;

        setIsLoading(true);
        try {
            const { data, error } = await audienceSignUp(firstName, lastName, email, password);
            if (error) {
                throw new Error(error.message || 'Signup has failed');
            }
            setShowVerificationMessage(true);
            setResendSuccess(false);
        } catch (error) {
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendVerification = async () => {
        if (isResendLoading) return;
        
        setIsResendLoading(true);
        try {
            const { error } = await resendAudienceVerificationEmail(email);
            if (error) {
                setResendSuccess(false);
                setError(error.message || 'Failed to resend verification email');
                return;
            }
            setResendSuccess(true);
            setError('');
        } catch (err) {
            setResendSuccess(false);
            setError('Failed to resend verification email. Please try again later.');
        } finally {
            setIsResendLoading(false);
        }
    };

    const commonTextFieldSx = {
        backgroundColor: '#333',
        borderRadius: '4px',
        width: '100%',
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: '#555',
            },
            '&:hover fieldset': {
                borderColor: '#666',
            },
        },
    };

    const getPrimaryMissingRequirement = () => {
        if (!firstName || !lastName) return "Please enter your full name";
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Please enter a valid email";
        if (!password || !validatePassword()) return "Password must meet all requirements";
        if (password !== confirmPassword) return "Passwords don't match";
        if (!acceptTerms) return "Please accept terms and conditions";
        return "";
    };

    return (
        <Container maxWidth="sm">
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    marginTop: '5rem',
                    padding: '2rem',
                    borderRadius: '8px',
                    backgroundColor: 'black',
                    color: 'white',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.5)',
                }}
            >
                <Box sx={{ width: '100%', marginBottom: '2rem' }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 600,
                            marginBottom: '0.5rem'
                        }}
                    >
                        Discover Independent Cinema
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            color: '#999',
                            textAlign: 'left',
                            fontSize: '1.1rem',
                            lineHeight: '1.5'
                        }}
                    >
                        Join our community to explore unique films, watch exclusive premieres, and support independent filmmakers worldwide
                    </Typography>
                </Box>

                {showVerificationMessage ? (
                    <Box sx={{
                        width: '100%',
                        marginBottom: '1rem',
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        borderRadius: '4px',
                        padding: '16px',
                        border: '1px solid rgba(255, 87, 34, 0.3)'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                            <CheckCircleOutlineIcon color="primary" sx={{ marginRight: '8px' }} />
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 500 }}>
                                Please Verify Your Email
                            </Typography>
                        </Box>
                        <Typography sx={{ color: '#fff', marginBottom: '8px', lineHeight: 1.5 }}>
                            We've sent a verification link to <Typography component="span" color="primary" sx={{ fontWeight: 500 }}>{email}</Typography>. Please check your inbox and click the link to verify your account.
                        </Typography>
                        <Typography sx={{ color: '#999', marginTop: '12px', fontSize: '0.875rem' }}>
                            Didn't receive the email? Check your spam folder or{' '}
                            <Button
                                color="primary"
                                sx={{
                                    padding: '0 4px',
                                    minWidth: 'auto',
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                        textDecoration: 'underline'
                                    }
                                }}
                                onClick={handleResendVerification}
                                disabled={isResendLoading}
                            >
                                {isResendLoading ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <CircularProgress size={16} color="primary" sx={{ mr: 1 }} />
                                        Sending...
                                    </Box>
                                ) : (
                                    'click here to resend'
                                )}
                            </Button>
                        </Typography>
                        {resendSuccess && (
                            <Alert
                                severity="success"
                                sx={{
                                    mt: 2,
                                    backgroundColor: 'rgba(46, 125, 50, 0.1)',
                                    '& .MuiAlert-message': {
                                        color: '#fff'
                                    }
                                }}
                            >
                                Verification email has been resent. Please check your inbox.
                            </Alert>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                href="/login"
                                sx={{
                                    textTransform: 'none',
                                    borderColor: 'rgba(33, 150, 243, 0.5)',
                                    '&:hover': {
                                        backgroundColor: 'primary.light', // Lighter shade of primary color
                                        boxShadow: (theme) => `0 4px 8px ${theme.palette.primary.main}40`, // Subtle glow with primary color
                                        transform: 'translateY(-1px)' // Slight lift effect
                                    }
                                }}
                            >
                                Back to Login
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <>
                        {error && (
                            <Alert severity="error" sx={{ width: '100%', marginBottom: '1rem', backgroundColor: 'rgba(211, 47, 47, 0.1)' }}>
                                {error}
                            </Alert>
                        )}

                        <Box sx={{ width: '100%', marginBottom: '1rem' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography sx={{ color: 'white', marginBottom: '4px' }}>
                                        First Name
                                    </Typography>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        sx={commonTextFieldSx}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography sx={{ color: 'white', marginBottom: '4px' }}>
                                        Last Name
                                    </Typography>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        sx={commonTextFieldSx}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        <Box sx={{ width: '100%', marginBottom: '1rem' }}>
                            <Typography sx={{ color: 'white', marginBottom: '4px' }}>
                                Email
                            </Typography>
                            <TextField
                                variant="outlined"
                                fullWidth
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                sx={commonTextFieldSx}
                            />
                        </Box>

                        <Box sx={{ width: '100%', marginBottom: '1rem' }}>
                            <Typography sx={{ color: 'white', marginBottom: '4px' }}>
                                Password
                            </Typography>
                            <TextField
                                type="password"
                                variant="outlined"
                                fullWidth
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                onFocus={() => setIsPasswordFocused(true)}
                                onBlur={() => setIsPasswordFocused(false)}
                                sx={commonTextFieldSx}
                            />

                            {isPasswordFocused && (
                                <List dense sx={{ mt: 1, bgcolor: 'rgba(0, 0, 0, 0.3)', borderRadius: '4px', p: 1 }}>
                                    {[
                                        { key: 'length', label: 'At least 8 characters' },
                                        { key: 'uppercase', label: 'At least one uppercase letter' },
                                        { key: 'lowercase', label: 'At least one lowercase letter' },
                                        { key: 'number', label: 'At least one number' },
                                        { key: 'special', label: 'At least one special character (@$!%*?&)' },
                                    ].map(({ key, label }) => (
                                        <ListItem key={key} sx={{ p: 0 }}>
                                            <ListItemIcon sx={{ minWidth: '30px' }}>
                                                {passwordRequirements[key] ? (
                                                    <CheckCircleOutlineIcon sx={{ color: 'green' }} fontSize="small" />
                                                ) : (
                                                    <RadioButtonUncheckedIcon sx={{ color: 'grey' }} fontSize="small" />
                                                )}
                                            </ListItemIcon>
                                            <Typography variant="body2" sx={{ color: passwordRequirements[key] ? 'green' : 'grey' }}>
                                                {label}
                                            </Typography>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Box>

                        <Box sx={{ width: '100%', marginBottom: '1rem' }}>
                            <Typography sx={{ color: 'white', marginBottom: '4px' }}>
                                Confirm Password
                            </Typography>
                            <TextField
                                type="password"
                                variant="outlined"
                                fullWidth
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                sx={commonTextFieldSx}
                            />
                        </Box>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={acceptTerms}
                                    onChange={(e) => setAcceptTerms(e.target.checked)}
                                    sx={{ color: 'white' }}
                                />
                            }
                            label={
                                <Typography sx={{ color: 'white' }}>
                                    I accept the{' '}
                                    <a
                                        href="https://www.kalakaar.io/terms/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: 'lightblue', textDecoration: 'none' }}
                                    >
                                        Terms and Conditions
                                    </a>
                                    {' '}and have read the{' '}
                                    <a
                                        href="https://www.kalakaar.io/privacy/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: 'lightblue', textDecoration: 'none' }}
                                    >
                                        Privacy Policy
                                    </a>
                                    .
                                </Typography>
                            }
                        />

                        <Box sx={{ width: '100%' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                sx={{ marginTop: '1rem', height: '42px' }}
                                onClick={handleSignup}
                                disabled={getPrimaryMissingRequirement() !== "" || isLoading}
                            >
                                {isLoading ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                                        Signing Up...
                                    </Box>
                                ) : (
                                    'Sign Up'
                                )}
                            </Button>
                            {getPrimaryMissingRequirement() && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'block',
                                        textAlign: 'center',
                                        mt: 1,
                                        color: 'error.main',
                                        fontSize: '0.8rem'
                                    }}
                                >
                                    {getPrimaryMissingRequirement()}
                                </Typography>
                            )}
                        </Box>

                        <Typography sx={{ marginTop: '1rem', color: 'white' }}>
                            Already have an account?{' '}
                            <a href="/login" style={{ color: 'lightblue', textDecoration: 'none' }}>
                                Log in
                            </a>
                        </Typography>
                    </>
                )}
            </Box>
        </Container>
    );
}

export default SignupPage;